import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { KTCard, KTCardBody, KTSVG } from '../../../../_metronic/helpers'
import { getDocById, createDoc, updateDoc, getCategories, createCategory } from './core/_requests'
import { Skeleton } from '../../../../_component/Skeleton'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import Editor from '../../../../_component/Editor'
import { MultiSelect } from '../../../../_component/MultiSelect'
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik'
import * as Yup from 'yup'

interface FormValues {
  title: string
  shortDesc: string
  description: string
  status: 'PUBLISHED' | 'DRAFT'
  categoryIds: (string | number)[]
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required').min(10, 'Description must be at least 10 characters'),
  categoryIds: Yup.array().min(1, 'Please select at least one category'),
})

const ValidationToast = () => {
  const { errors, submitCount } = useFormikContext()
  React.useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]
      if (typeof firstError === 'string') {
        toast.error(firstError)
      }
    }
  }, [submitCount])
  return null
}

const DocEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = id !== undefined

  const { data: categories } = useQuery(['categories'], getCategories)
  const { data: doc, isLoading: isDocLoading } = useQuery(
    ['doc', id],
    () => getDocById(id),
    { enabled: isEdit }
  )

  const createCatMutation = useMutation(createCategory, {
    onSuccess: (newCat) => {
      queryClient.invalidateQueries(['categories'])
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create category'
      toast.error(message)
    },
  })

  const saveMutation = useMutation(
    (values: FormValues) => (isEdit ? updateDoc(id!, values as any) : createDoc(values as any)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['docs'])
        navigate('/docs')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} document`
        toast.error(message)
      },
    }
  )

  const initialValues: FormValues = useMemo(() => {
    if (isEdit && doc) {
      return {
        title: doc.title || '',
        shortDesc: doc.shortDesc || '',
        description: doc.description || '',
        status: doc.status || 'DRAFT',
        categoryIds: (doc.categories?.map((c) => c.id).filter((id) => id !== undefined) as (string | number)[]) || [],
      }
    }
    return {
      title: '',
      shortDesc: '',
      description: '',
      status: 'DRAFT',
      categoryIds: [],
    }
  }, [doc, isEdit])

  if (isEdit && isDocLoading) {
    return (
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='bg-white border border-gray-200 rounded-lg shadow-sm'>
            <div className='card-header border-0 pt-5 border-l-4 border-[#008080] rounded-tl-md'>
              <h3 className='card-title align-items-start flex-column'>
                <Skeleton width={200} height={24} />
              </h3>
            </div>
            <div className='p-9'>
              <div className='d-flex flex-column gap-7'>
                <div>
                  <Skeleton width={60} height={14} className='mb-2' />
                  <Skeleton width='100%' height={42} className='rounded-md' />
                </div>
                <div>
                  <Skeleton width={80} height={14} className='mb-2' />
                  <Skeleton width='100%' height={42} className='rounded-md' />
                </div>
                <div>
                  <Skeleton width={80} height={14} className='mb-2' />
                  <Skeleton width='100%' height={300} className='rounded-md' />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-12'>
        <KTCard className='border-gray-200 shadow-sm'>
          <Formik<FormValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={({ categoryIds, ...rest }) => {
              saveMutation.mutate({ ...rest, category_ids: categoryIds } as any)
            }}
          >
            {({ values, setFieldValue, submitForm }) => {
              return (
                <Form>
                  <ValidationToast />
                  <>
                    <div className='card-header border-0 pt-5 border-l-4 border-[#008080] rounded-tl-md'>
                      <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bold fs-3 mb-1'>
                          {isEdit ? `Editing — ${doc?.title}` : 'New Doc'}
                        </span>
                      </h3>
                      <div className='card-toolbar'>
                        <button
                          type='button'
                          className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                          onClick={() => navigate('/docs')}
                          title='Back to docs'
                        >
                          <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='w-4 h-4' />
                        </button>
                      </div>
                    </div>

                    <KTCardBody className='py-3'>
                      <div className='d-flex flex-column gap-7 px-5 py-5'>
                        <div className='fv-row mb-5'>
                          <label className='required form-label text-lg fw-semibold text-gray-700 mb-2 mr-2'>
                            Title
                          </label>
                          <Field
                            type='text'
                            name='title'
                            className='form-control form-control-solid focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080] transition-all py-2 px-6 w-1/2 rounded-lg border border-gray-200'
                            placeholder='Enter document title'
                          />
                        </div>

                        <div className='mb-5'>
                          <label className='required form-label fw-semibold text-gray-700 mb-2'>
                            Categories
                          </label>
                          <MultiSelect
                            placeholder='Select categories'
                            options={categories?.map((c) => ({ value: c.id, label: c.name })) || []}
                            selected={categories
                              ?.filter((c) => c.id !== undefined && c.id !== null && (values.categoryIds as any).includes(c.id))
                              .map((c) => ({ value: c.id, label: c.name }))}
                            onSelect={(v) => setFieldValue('categoryIds', v.map((item) => item.value))}
                            createFn={(val) =>
                              createCatMutation.mutate({ name: val }, {
                                onSuccess: (newCat) => {
                                  setFieldValue('categoryIds', [...values.categoryIds, newCat.id])
                                }
                              })
                            }
                          />
                        </div>

                        <div className='mb-5'>
                          <label className='block mb-2 text-sm font-semibold text-gray-700'>
                            Short Description
                            <span className='text-gray-400 font-normal text-xs ml-2'>(optional)</span>
                          </label>
                          <textarea
                            name='shortDesc'
                            value={values.shortDesc}
                            onChange={(e) => {
                              if (e.target.value.length <= 200) {
                                setFieldValue('shortDesc', e.target.value)
                              }
                            }}
                            rows={3}
                            className={`block w-full text-base py-2.5 px-4 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all resize-none ${
                              values.shortDesc.length >= 200
                                ? 'border-red-400 focus:ring-red-300/30 focus:border-red-400'
                                : 'border-gray-200 focus:ring-[#008080]/30 focus:border-[#008080]'
                            }`}
                            placeholder='Brief summary of this document'
                          />
                          <div className='flex justify-between items-center mt-1.5'>
                            {values.shortDesc.length >= 200 ? (
                              <span className='text-xs text-red-500'>Character limit reached</span>
                            ) : (
                              <span />
                            )}
                            <span className={`text-xs ${values.shortDesc.length >= 200 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                              {values.shortDesc.length}/200
                            </span>
                          </div>
                        </div>

                        <div className='mb-5'>
                          <label className='required form-label fw-semibold text-gray-700 mb-2'>
                            Description
                          </label>
                          <Editor name='description' />
                        </div>

                        <div className='flex justify-content-end gap-3 mt-5'>
                          <button
                            type='button'
                            className='inline-flex items-center gap-5 px-4 py-2 bg-[#ccf2f2] text-[#008080] text-sm font-medium rounded-lg hover:bg-[#b3e6e6] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                            onClick={async () => {
                              await setFieldValue('status', 'DRAFT')
                              submitForm()
                            }}
                            disabled={saveMutation.isLoading}
                          >
                            {saveMutation.isLoading ? 'Saving...' : 'Save as Draft'}
                          </button>
                          <button
                            type='button'
                            className='inline-flex items-center gap-2 px-4 py-2 bg-[#008080] text-white text-sm font-medium rounded-lg hover:bg-[#007373] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                            onClick={async () => {
                              await setFieldValue('status', 'PUBLISHED')
                              submitForm()
                            }}
                            disabled={saveMutation.isLoading}
                          >
                            {saveMutation.isLoading ? 'Publishing...' : 'Publish'}
                          </button>
                        </div>
                      </div>
                    </KTCardBody>
                  </>
                </Form>
              )
            }}
          </Formik>
        </KTCard>
      </div>
    </div>
  )
}

export default DocEdit
